
import { SystemEvent } from '../types';

export interface TemplateVariable {
  key: string;
  type: 'string' | 'number' | 'date' | 'link' | 'boolean';
  source: string;
  example: string;
  description: string;
  required: boolean;
}

export interface OrchestratorInfo {
  method: string;
  triggerSource: string;
  description: string;
  injectedContext: string[];
}

/**
 * Canonical Variable Registry: The authoritative list of all context shards available for Payload Rendering.
 */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { key: 'userName', type: 'string', source: 'User Profile', example: 'Emmanuel', description: 'Display name of the triggering operator', required: true },
  { key: 'userEmail', type: 'string', source: 'Auth Registry', example: 'operator@priori-ai.com', description: 'Primary identity of the user', required: true },
  { key: 'userId', type: 'string', source: 'Auth Registry', example: 'z1x2c3v4', description: 'Internal system identifier for the user', required: true },
  { key: 'appUrl', type: 'link', source: 'System Constants', example: 'https://priori.ai', description: 'Base URL for deep-linking into the Grid', required: true },
  { key: 'taskTitle', type: 'string', source: 'Mission Logic', example: 'Refactor Auth Shard', description: 'Title of the associated objective', required: false },
  { key: 'taskId', type: 'string', source: 'Mission Logic', example: 'z8x2k1', description: 'Unique identifier for the mission', required: false },
  { key: 'quadrant', type: 'string', source: 'Mission Logic', example: 'Q2', description: 'Eisenhower classification (Q1-Q4)', required: false },
  { key: 'subtaskTitle', type: 'string', source: 'Decomposition Shard', example: 'Secure API Endpoints', description: 'Title of the specific sub-objective updated', required: false },
  { key: 'subtaskStatus', type: 'string', source: 'Decomposition Shard', example: 'COMPLETED', description: 'Current state of the sub-objective', required: false },
  { key: 'timestamp', type: 'date', source: 'System Runtime', example: '10/24/2024, 14:30', description: 'Human-readable time the event occurred', required: true },
  { key: 'node', type: 'string', source: 'Relay Hub', example: 'Accountability Relay', description: 'Originating infrastructure node', required: false },
  { key: 'diagnostic', type: 'string', source: 'Orchestration Error', example: 'Auth Link Unstable', description: 'Context regarding system failures', required: false },
  { key: 'amount', type: 'string', source: 'Stripe Relay', example: '$10.00', description: 'Invoice payment amount', required: false },
  { key: 'streakCount', type: 'number', source: 'Analytics Node', example: '7', description: 'Current consecutive active days', required: false },
  { key: 'pulseRequestUrl', type: 'link', source: 'Accountability Relay', example: 'https://priori.ai/#/partner-action?uid=...', description: 'Deep-link for a partner to request an update', required: false },
  { key: 'customTitle', type: 'string', source: 'Admin Pulse', example: 'System Maintenance', description: 'Title of the manual broadcast', required: false },
  { key: 'customMessage', type: 'string', source: 'Admin Pulse', example: 'Downtime expected...', description: 'Body content of the broadcast', required: false },
  { key: 'coachInsight', type: 'string', source: 'Gemini Coach (Server)', example: 'Your strategic wins today show real momentum...', description: 'AI-generated daily affirmation based on task metrics and recent completions', required: false },
  { key: 'message', type: 'string', source: 'Momentum Engine (Server)', example: 'You have 2 Urgent crises and 3 Strategic objectives...', description: 'Operational readiness summary for daily pulse emails', required: false }
];

/**
 * Orchestration Vectors: Documentation for system methods that initiate the Relay Loop.
 */
export const ORCHESTRATORS: OrchestratorInfo[] = [
  {
    method: 'processEvent',
    triggerSource: 'System Core / Auth / Admin / Stripe',
    description: 'The primary orchestration vector for general system notifications and lifecycle events.',
    injectedContext: ['userName', 'userEmail', 'userId', 'timestamp', 'node', 'appUrl']
  },
  {
    method: 'triggerDailyMomentum',
    triggerSource: 'Server Cron (7 PM local)',
    description: 'Server-side daily momentum cycle. Generates AI Coach Insight via Gemini and operational readiness summary per user.',
    injectedContext: ['userName', 'userEmail', 'userId', 'appUrl', 'message', 'coachInsight']
  },
  {
    method: 'notifyPartners',
    triggerSource: 'Accountability Relay',
    description: 'Triggered during mission updates to inform linked accountability partners.',
    injectedContext: ['userName', 'userEmail', 'userId', 'taskTitle', 'taskId', 'quadrant', 'subtaskTitle', 'subtaskStatus', 'occurredAt', 'appUrl', 'pulseRequestUrl']
  }
];

/**
 * AUTHORITATIVE DEPENDENCY MAP
 * Links systemEvents to the specific variables required for successful Hop 2 (Association Resolution).
 */
export const EVENT_REQUIRED_CONTEXT: Record<SystemEvent | string, string[]> = {
  'welcome_new_user': ['userId', 'userEmail', 'userName', 'appUrl'],
  'profile_deleted': ['userEmail', 'userName', 'appUrl'],
  'created': ['userId', 'userEmail', 'userName', 'taskId', 'taskTitle', 'quadrant', 'appUrl'],
  'subtask': ['userId', 'userEmail', 'userName', 'taskId', 'taskTitle', 'subtaskTitle', 'subtaskStatus', 'appUrl'],
  'completed': ['userId', 'userEmail', 'userName', 'taskId', 'taskTitle', 'quadrant', 'appUrl'],
  'partner_update': ['userName', 'userEmail', 'taskTitle', 'appUrl'],
  'partner_request': ['userName', 'taskTitle', 'quadrant', 'pulseRequestUrl', 'appUrl'],
  'daily': ['userId', 'userEmail', 'userName', 'appUrl', 'message', 'coachInsight'],
  'inactivity_7_days': ['userName', 'appUrl'],
  'streak_7_day': ['userName', 'appUrl'],
  'invoice_paid': ['userName', 'amount', 'appUrl'],
  'payment_failed': ['userName', 'appUrl'],
  'subscription_canceled': ['userName', 'appUrl'],
  'broadcast': ['userName', 'userEmail', 'customTitle', 'customMessage', 'appUrl']
};